'use client'

import { Controller, useFormContext } from 'react-hook-form'
import CheckIcon from '@mui/icons-material/Check'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import Typography from '@mui/material/Typography'

import type { FlowFieldConfig, FlowFieldOption } from '@/src/lib/flow-engine/types'

interface CityGridFieldProps {
  field: FlowFieldConfig
  options: FlowFieldOption[]
  loading: boolean
  onFieldLabelResolved?: (_labels: Record<string, string>) => void
}

export const CityGridField: React.FC<CityGridFieldProps> = ({
  field,
  options,
  loading,
  onFieldLabelResolved
}) => {
  const {
    control,
    formState: { errors }
  } = useFormContext()
  const sortedOptions = [...options].sort((a, b) => a.label.localeCompare(b.label, 'it'))
  const error = errors[field.key]
  const errorMessage = error && 'message' in error ? (error.message as string) : undefined

  const imageBasePath = field.remoteOptions?.imageBasePath ?? '/images'

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Controller
      name={field.key}
      control={control}
      render={({ field: f }) => (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
                md: 'repeat(5, 1fr)'
              },
              gap: 4
            }}
          >
            {sortedOptions.map((opt, index) => {
              const isSelected = f.value === opt.value
              const optionKey = `${opt.value}-${opt.label}-${index}`
              return (
                <Box key={optionKey} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box
                    onClick={() => {
                      f.onChange(opt.value)
                      onFieldLabelResolved?.({ [field.key]: opt.label })
                    }}
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={opt.label}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        f.onChange(opt.value)
                        onFieldLabelResolved?.({ [field.key]: opt.label })
                      }
                    }}
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      outline: isSelected ? '3px solid' : '2px solid transparent',
                      outlineColor: isSelected ? 'primary.main' : 'transparent',
                      transition: 'outline-color 0.2s, box-shadow 0.2s',
                      aspectRatio: '195/115',
                      '&:hover': {
                        boxShadow: 4
                      },
                      '&:focus-visible': {
                        outlineColor: 'primary.main',
                        outlineWidth: '0.3rem',
                        outlineStyle: 'solid'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={`${imageBasePath}/${opt.label.toLowerCase().replace(/[\s']/g, '-')}.jpg`}
                      alt={opt.label}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                    {/* Gradient overlay + city name */}
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 50%)',
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: 1.5
                      }}
                    />

                    {/* Selected check */}
                    {isSelected && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '0.8rem',
                          right: '0.8rem',
                          width: '2.4rem',
                          height: '2.4rem',
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.contrastText',
                          fontSize: '1.4rem',
                          fontWeight: 700
                        }}
                      >
                        <CheckIcon sx={{ width: '1.4rem', height: '1.4rem' }} />
                      </Box>
                    )}
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: { xs: '1.2rem', sm: '1.36rem' }
                    }}
                  >
                    {opt.label}
                  </Typography>
                </Box>
              )
            })}
          </Box>
          {errorMessage && (
            <FormHelperText error sx={{ mt: 1 }}>
              {errorMessage}
            </FormHelperText>
          )}
        </Box>
      )}
    />
  )
}
